import { useAtomValue } from "jotai";
import { Fragment, useEffect, useState } from "react";
import {
  FindingsSection,
  type PanelJump,
  type SliceLoaded,
} from "@/components/devicePanel/FindingsSection";
import { CallChip } from "@/components/devicePanel/CallChip";
import { Hint } from "@/components/devicePanel/Hint";
import { DeviceState } from "@/components/devicePanel/DeviceState";
import {
  ReviewProvider,
  ReviewRow,
  ReviewTags,
  rowAt,
  useIsShown,
  type ReviewModel,
} from "@/components/devicePanel/Review";
import { instanceSliceInfoMapAtom } from "@/atoms/selectionAtoms";
import { slotAssignmentsSelector } from "@/atoms/hangingProtocolAtoms";
import { FaInfoCircle } from "@/utils/icons";
import type { WindowLevel } from "@/components/CaseViewer";
import { findingWindow } from "@/constants/windowPresets";
import { roundOf, type ReviewTarget, type RowPosition } from "@/types/devicePanelReview";
import {
  WORDS,
  callLine,
  findingCalls,
  findingsSections,
  qualifiedName,
  readCall,
  triageDevices,
  type FindingCalls,
  type DevicePanelFinding,
  type DevicePanelStudyDevice,
  type DevicePanelStudyResponse,
} from "@/types/devicePanel";

/** A calling device as the tooltip names it: on a grouped finding with what its
 * call means there, "Omni (moderate+)"; a grouped call without its qualifier
 * says so rather than guess. */
function callerName(device: DevicePanelStudyDevice, finding: DevicePanelFinding) {
  if (finding.group === null) return device.short;
  return (
    qualifiedName(device.short, finding.group.qualifier) ?? `${device.short} (qualifier missing)`
  );
}

/** One finding by name, with the strongest call's word only when it is not
 * Flagged (consider, below, incomplete): a flagged row is the plain case. Its
 * tooltip has a line per device that calls it, with that device's name for it
 * and reading. A call that clears a stricter point than its default gets a
 * full-weight name; a consider call's name is muted. A call kept only because
 * it is queued for review (below every allowed point) reads "below". `divider`
 * rules it off from the Flagged rows above. Verdict controls sit under it, one
 * per device call. */
function FindingRow({
  finding: { key, display_name, calls, strongest },
  divider,
  sliceLoaded,
  onJump,
}: {
  finding: FindingCalls;
  divider: boolean;
  sliceLoaded: SliceLoaded;
  onJump: PanelJump;
}) {
  const read = readCall(strongest.finding);
  const call = read.kind === "hidden" ? ({ kind: "below" } as const) : read;
  const keySlice = strongest.finding.top_slices[0];
  return (
    <ReviewRow
      targets={calls.map(({ device, finding }) => ({
        device: device.key,
        condition: finding.condition,
      }))}
      labels={calls.map(({ device }) => device.short)}
      indent="pl-3"
      ready={!keySlice || sliceLoaded(keySlice)}
    >
      <Hint
        label={
          <span className="grid gap-0.5">
            {calls.map(({ device, finding }) => (
              <span key={device.key}>
                {callLine(callerName(device, finding), finding.display_name, finding)}
              </span>
            ))}
          </span>
        }
      >
        <button
          type="button"
          aria-disabled={!keySlice}
          onClick={() =>
            keySlice &&
            onJump(
              keySlice,
              calls.map(({ finding }) => finding.condition),
            )
          }
          data-testid="triage-finding"
          data-jump
          data-finding={key}
          data-call={call.kind}
          data-callers={calls.map(({ device, finding }) => callerName(device, finding)).join(",")}
          className={`flex w-full items-center gap-2.5 border-t px-3 py-2 text-left hover:bg-surface focus-visible:outline focus-visible:outline-1 focus-visible:-outline-offset-1 focus-visible:outline-signal aria-disabled:cursor-default ${
            divider ? "border-dashed border-border-strong" : "border-border"
          }`}
        >
          <span
            className={`text-[14px] ${
              call.kind !== "flagged"
                ? "text-fg-muted"
                : call.stricter
                  ? "font-medium text-fg"
                  : "text-fg"
            }`}
          >
            {display_name}
            <ReviewTags
              targets={calls.map(({ device, finding }) => ({
                device: device.key,
                condition: finding.condition,
              }))}
            />
          </span>
          {call.kind !== "flagged" && <CallChip kind={call.kind} />}
        </button>
      </Hint>
    </ReviewRow>
  );
}

/** The devices' findings, one row each: Flagged first, then a dashed divider and
 * the consider (and incomplete) rows. */
function FindingRows({
  devices,
  sliceLoaded,
  onJump,
}: {
  devices: DevicePanelStudyDevice[];
  sliceLoaded: SliceLoaded;
  onJump: PanelJump;
}) {
  const isShown = useIsShown();
  const findings = findingCalls(devices, ({ device, finding }) =>
    isShown({ device: device.key, condition: finding.condition }),
  );
  const flagged = (index: number) =>
    index >= 0 && readCall(findings[index].strongest.finding).kind === "flagged";
  return findings.map((finding, index) => (
    <FindingRow
      key={finding.key}
      finding={finding}
      divider={flagged(index - 1) && !flagged(index)}
      sliceLoaded={sliceLoaded}
      onJump={onJump}
    />
  ));
}

/** Each device with no readable result as "Omni: running" (reason in its tooltip). */
function UnreadDevices({ devices }: { devices: DevicePanelStudyDevice[] }) {
  return devices.map(
    (device) =>
      device.state !== "completed" && (
        <span key={device.key}>
          {device.short}:{" "}
          <DeviceState state={device.state} message={device.message} className="lowercase" />
        </span>
      ),
  );
}

/**
 * The legend beside the Triage heading: each in-scope device's name (with its
 * state when it has no readable result), then the not-applicable devices, a
 * toggle for their review-only rows with the reason in its tooltip.
 */
function TriageLegend({
  region,
  inScope,
  notApplicable,
  open,
  onToggle,
}: {
  region: string;
  inScope: DevicePanelStudyDevice[];
  notApplicable: DevicePanelStudyDevice[];
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <p
      data-testid="triage-legend"
      className="ml-auto flex flex-wrap items-center justify-end gap-x-3 gap-y-1 text-[11px] text-fg-muted"
    >
      {inScope.map((device) => (
        <span key={device.key} className="whitespace-nowrap">
          {device.short}
          {device.state !== "completed" && (
            <>
              : <DeviceState state={device.state} message={device.message} className="lowercase" />
            </>
          )}
        </span>
      ))}
      {notApplicable.length > 0 && (
        <Hint
          label={`Not applicable: these devices ran on this ${region} study but are built for another region. Their output is shown for review only and does not count as a result.`}
        >
          <button
            type="button"
            aria-expanded={open}
            onClick={onToggle}
            className="text-fg-dim hover:text-fg focus-visible:outline focus-visible:outline-1 focus-visible:outline-signal"
          >
            {notApplicable.map((device) => device.short).join(", ")} n/a
          </button>
        </Hint>
      )}
    </p>
  );
}

/**
 * Research view of every device on one study: the triage findings on top, each
 * by name with the devices that call it in its tooltip, under a legend naming
 * each device once (not-applicable devices fold into it and are
 * never counted as a result), then each findings device's list, demoted, with
 * the device compared against it behind a toggle. Every row jumps the viewer to its
 * key slice. Devices take their place from their configured role.
 */
export function DevicePanelRail({
  study,
  review,
  link,
  jumpToSlice,
}: {
  study: DevicePanelStudyResponse;
  /** The study's reader review; null while it loads. */
  review: ReviewModel | null;
  /** The finding a deep link names, or the row position it opens at (resolved
   * once the review rows render): that row scrolls into view, takes focus and
   * jumps to its key slice. */
  link: ReviewTarget | RowPosition | null;
  jumpToSlice: (sopInstanceUid: string, window: WindowLevel) => void;
}) {
  const sliceInfo = useAtomValue(instanceSliceInfoMapAtom);
  const viewerReady = Object.values(useAtomValue(slotAssignmentsSelector)).some(Boolean);
  const sliceLoaded: SliceLoaded = (sopInstanceUid) =>
    Object.values(sliceInfo).some((instances) => sopInstanceUid in instances);
  // Every jump shows its finding at the window its anatomy calls for.
  const jump: PanelJump = (sopInstanceUid, conditions) =>
    jumpToSlice(sopInstanceUid, findingWindow(conditions));
  const [positioned, setPositioned] = useState<ReviewTarget | null>(null);
  useEffect(() => {
    if (typeof link === "string" && review && !positioned) setPositioned(rowAt(link));
  }, [link, review, positioned]);
  const linked = typeof link === "string" ? positioned : link;
  const { inScope, notApplicable } = triageDevices(study.devices);
  const [showNotApplicable, setShowNotApplicable] = useState(false);
  // Queued review items and the linked finding are always visible: the
  // not-applicable rows stay open while one of them is there.
  const notApplicableQueued = [
    ...(review?.data.items.filter((item) => roundOf(item.kind) === review.round) ?? []),
    ...(linked ? [linked] : []),
  ].some((target) => notApplicable.some((device) => device.key === target.device));
  const notApplicableOpen = showNotApplicable || notApplicableQueued;
  // The info tooltip explains only the words the triage devices' calls can show
  // here: a panel without below-default calls has no "consider" to explain.
  const kinds = new Set(
    [...inScope, ...notApplicable].flatMap((device) =>
      (device.findings ?? []).map((finding) => readCall(finding).kind),
    ),
  );
  // Flagged is the plain row, so only the other words need explaining.
  const words = WORDS.filter(({ kind }) => kind !== "flagged" && kinds.has(kind));

  return (
    <ReviewProvider review={review} link={linked} viewerReady={viewerReady}>
      <div data-device-panel-rail className="flex h-full flex-col bg-bg-2 text-fg">
        <section aria-label="Triage" className="max-h-[50%] shrink-0 overflow-y-auto">
          <div className="flex items-center gap-1.5 border-b border-border px-3 pb-2 pt-2.5">
            <h2 className="text-[13px] font-semibold">Triage</h2>
            {words.length > 0 && (
              <Hint
                label={
                  <span className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-2 gap-y-1">
                    {words.map(({ kind, meaning }) => (
                      <Fragment key={kind}>
                        <CallChip kind={kind} />
                        <span>{meaning}</span>
                      </Fragment>
                    ))}
                  </span>
                }
              >
                <button
                  type="button"
                  aria-label="What the words mean"
                  className="rounded-full text-fg-dim hover:text-fg focus-visible:outline focus-visible:outline-1 focus-visible:outline-signal"
                >
                  <FaInfoCircle className="h-3 w-3" />
                </button>
              </Hint>
            )}
            <TriageLegend
              region={study.region}
              inScope={inScope}
              notApplicable={notApplicable}
              open={notApplicableOpen}
              onToggle={() => setShowNotApplicable(!notApplicableOpen)}
            />
          </div>
          <FindingRows devices={inScope} sliceLoaded={sliceLoaded} onJump={jump} />
          {notApplicableOpen && (
            <div
              data-testid="not-applicable"
              className="border-t border-dashed border-border-strong opacity-60"
            >
              <FindingRows devices={notApplicable} sliceLoaded={sliceLoaded} onJump={jump} />
              <p className="flex flex-wrap gap-x-3 px-3 py-1.5 text-[11px] text-fg-dim empty:hidden">
                <UnreadDevices devices={notApplicable} />
              </p>
            </div>
          )}
        </section>
        {findingsSections(study.devices).map(({ device, compare }) => (
          <FindingsSection
            key={device.key}
            device={device}
            compare={compare}
            sliceLoaded={sliceLoaded}
            onJump={jump}
          />
        ))}
      </div>
    </ReviewProvider>
  );
}
